/**
 * Hooks de la file de modération (lot 5) : la file, son total pour le menu,
 * et les actions de chaque type, qui rafraîchissent la file après coup.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEnvironment } from '../../../contexts/EnvironmentContext';
import { useNotify } from '../../../contexts/NotificationContext';
import { CACHE_TTL } from '../../../config/queryConfig';
import ModerationApi from '../../../services/moderationMessagerieApi';
import IaSignalementsApiService from '../../../services/iaSignalementsApi';
import ExerciseReportApiService from '../../../services/exerciseReportApi';
import { lireFileATraiter } from '../services/aTraiterApi';
import { CLE_FILE, RAFRAICHISSEMENT_MS } from '../libelles';

/** File complète de l'environnement affiché. */
export const useATraiter = () => {
  const { environment: env } = useEnvironment();
  return useQuery({
    queryKey: [CLE_FILE, env],
    queryFn: () => lireFileATraiter(env),
    staleTime: CACHE_TTL.LIVE,
  });
};

/** Total à traiter, pour la pastille du menu (rafraîchi toutes les 60 s). */
export const useNombreATraiter = (actif = true): number => {
  const { environment: env } = useEnvironment();
  const { data } = useQuery({
    queryKey: [CLE_FILE, env],
    queryFn: () => lireFileATraiter(env),
    refetchInterval: RAFRAICHISSEMENT_MS,
    staleTime: CACHE_TTL.LIVE,
    enabled: actif,
  });
  return data?.compteurs.total ?? 0;
};

/** Actions disponibles sur un élément de la file. */
export type Action =
  | { type: 'valider' | 'supprimer' | 'traiterDetresse' | 'traiterIa' | 'resoudreExercice'; id: string }
  | { type: 'clore'; id: string; note: string };

/** Message de réussite de chaque action. */
const SUCCES: Record<Action['type'], string> = {
  valider: 'Message validé',
  supprimer: 'Message supprimé',
  traiterDetresse: 'Alerte marquée traitée',
  traiterIa: 'Signalement marqué traité',
  resoudreExercice: 'Signalement résolu',
  clore: 'Incident clos (la preuve reste conservée)',
};

/** Exécute une action sur l'environnement affiché, puis rafraîchit la file. */
export const useActionATraiter = () => {
  const { environment: env } = useEnvironment();
  const queryClient = useQueryClient();
  const { notify, notifyError } = useNotify();
  return useMutation({
    mutationFn: async (action: Action) => {
      switch (action.type) {
        case 'valider': return ModerationApi.valider(env, action.id);
        case 'supprimer': return ModerationApi.supprimer(env, action.id);
        case 'traiterDetresse': return ModerationApi.traiterDetresse(env, action.id);
        case 'traiterIa': return IaSignalementsApiService.setTraite(env, action.id, true);
        case 'resoudreExercice': return ExerciseReportApiService.resolveReport(env, action.id);
        case 'clore': return ModerationApi.cloreIncident(env, action.id, action.note);
      }
    },
    onSuccess: (_resultat, action) => {
      queryClient.invalidateQueries({ queryKey: [CLE_FILE] });
      notify(SUCCES[action.type], 'success');
    },
    onError: (erreur) => {
      // 409 « déjà décidé » : un autre admin est passé avant ; la file se met à jour.
      queryClient.invalidateQueries({ queryKey: [CLE_FILE] });
      notifyError(erreur, 'Action impossible');
    },
  });
};
